import { Group, Select, Tooltip, useMantineTheme } from '@mantine/core';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import { forwardRef, useMemo } from 'react';
import { IAutomationType } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import { IconLock } from '@tabler/icons-react';
import { FEATURE_TOGGLE_REVISION_PIPELINE_GATHER_CONFIRMATION } from 'env';

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  group: string;
  help?: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(({ label, help, ...others }: ItemProps, ref) => {
  return (
    <Group ref={ref} {...others} spacing={8}>
      <span>{label}</span>
      {help && <QuestionMarkTooltip label={help} />}
    </Group>
  );
});

/**
 * Map from every automation type to its name.
 */
export const AUTOMATION_TYPE_NAMES: Record<IAutomationType, ItemProps> = {
  'set-revision-safety-point': { label: 'Nastavenie záveru revíznej správy', group: 'Revízna správa' },
  'gather-confirmation': { label: 'Potvrdenie treťou osobou', group: 'Revízna správa' },
  'plan-next-revision': { label: 'Preplánovanie termínov zariadení', group: 'Termíny' },
  'set-device-evidence-field': { label: 'Nastavenie evidenčného poľa zariadenia', group: 'Zariadenia' },
  'send-email': { label: 'Odoslanie e-mailu', group: 'Notifikácie' },
  'set-file-name': { label: 'Nastavenie názvu PDF súboru', group: 'Revízna správa' },
  'create-child-department': { label: 'Vytvorenie podradeného objektu', group: 'Objekty' },
};

/**
 * Options for automation type select.
 */
const OPTIONS = Object.entries(AUTOMATION_TYPE_NAMES).map(([value, props]) => ({
  value: value as IAutomationType,
  ...props,
}));

/**
 * Map from every automation type to boolean value indicating whether the automation type is enabled.
 */
export const ALL_TYPES_ON: Record<IAutomationType, boolean> = {
  'set-revision-safety-point': true,
  'gather-confirmation': true,
  'plan-next-revision': true,
  'set-device-evidence-field': true,
  'send-email': true,
  'set-file-name': true,
  'create-child-department': true,
};

/**
 * Parameters of the AutomationTypeSelect component.
 */
export interface AutomationTypeSelectProps {
  value: IAutomationType;
  onChange: (value: IAutomationType) => void;
  readOnly?: boolean;
  automationTypes?: Partial<Record<IAutomationType, boolean>>;
}

/**
 * Select for automation type.
 */
export default function AutomationTypeSelect({
  value,
  onChange,
  readOnly = false,
  automationTypes = ALL_TYPES_ON,
}: AutomationTypeSelectProps) {
  const theme = useMantineTheme();

  const maskedAutomationTypes = useMemo(
    () => ({
      ...automationTypes,
      'gather-confirmation':
        automationTypes['gather-confirmation'] && FEATURE_TOGGLE_REVISION_PIPELINE_GATHER_CONFIRMATION,
    }),
    [automationTypes]
  );

  const filteredOptions = useMemo(
    () => OPTIONS.filter((option) => maskedAutomationTypes[option.value]),
    [maskedAutomationTypes]
  );

  return (
    <Select
      data={filteredOptions}
      size="lg"
      value={value}
      onChange={onChange}
      searchable
      readOnly={readOnly}
      disabled={readOnly}
      itemComponent={SelectItem}
      label={
        <Group spacing={4}>
          <span>Typ procesu</span>
          {readOnly && (
            <Tooltip label="Typ procesu už nie je možné upraviť" withArrow={false} openDelay={200}>
              <IconLock size={16} color={theme.colors.gray[6]} />
            </Tooltip>
          )}
        </Group>
      }
    />
  );
}
